import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  Container, 
  Grid as MuiGrid, 
  Typography, 
  Box, 
  Button, 
  Divider, 
  IconButton,
  Breadcrumbs,
  Link,
  Paper,
  Chip,
  Alert,
  TextField,
  Stack,
  Card,
  CardContent,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  ButtonGroup
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeIcon from '@mui/icons-material/Code';
import { productsApi } from '../api/api';
import { Product, Category } from '../types/api';
import { useCart } from '../context/CartContext';
import Loader from '../components/Loader';
import ErrorDisplay from '../components/ErrorDisplay';
import DeleteIcon from '@mui/icons-material/Delete';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import { useTheme } from '@mui/material/styles';
import AddToCartButton from '../components/AddToCartButton';
import { calculateFees as calculateProductFees } from '../utils/pricing';
import FavoriteButton from '../components/FavoriteButton';

// Define Grid component with proper React memo to prevent hooks issues
const Grid = React.memo(function Grid(props: any) {
  return <MuiGrid {...props} />;
});

// Debug mode check
const isDebugMode = process.env.REACT_APP_DEBUG_MODE === 'TRUE';

const ProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const { items: cartItems, addToCart, updateQuantity, removeFromCart } = useCart();
  const theme = useTheme();
  
  // Find if this product is already in the cart
  const cartItem = useMemo(() => {
    if (!id) return undefined;
    return cartItems.find(item => 
      item.id === id || 
      (item.productId && item.productId.toString() === id)
    );
  }, [cartItems, id]);
  
  // Set initial quantity to match cart if item exists
  useEffect(() => {
    if (cartItem) {
      setQuantity(cartItem.quantity);
    } else {
      setQuantity(1);
    }
  }, [cartItem]);

  const { data: product, isLoading, error } = useQuery<Product>({
    queryKey: ['product', id],
    queryFn: () => {
      if (!id) throw new Error('Product ID is required');
      console.log('Fetching product with ID:', id);
      return productsApi.getProduct(Number(id));
    },
    enabled: !!id,
    retry: 1
  });

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newQuantity = parseInt(event.target.value);
    if (newQuantity > 0) {
      setQuantity(newQuantity);
      
      // Update cart item if it exists
      if (cartItem) {
        updateQuantity(cartItem.id, newQuantity);
      }
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    if (cartItem) {
      // Update existing cart item
      updateQuantity(cartItem.id, quantity);
    } else {
      // Add new item to cart
      addToCart(product, quantity);
    }
    
    toast.success(`${cartItem ? 'Updated' : 'Added'} ${product.name} in cart`, {
      position: 'bottom-right',
      duration: 2000
    });
  };
  
  const handleRemoveFromCart = () => {
    if (id && cartItem) {
      removeFromCart(cartItem.id);
      setQuantity(1);
    }
  };

  // Calculate fees when product data is available
  const fees = useMemo(() => {
    if (!product) return null;
    return calculateProductFees(product);
  }, [product]);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Loader message="Loading product details..." />
      </Container>
    );
  }

  if (error || !product) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorDisplay 
          message="We couldn't find the product you're looking for."
          error={error as Error}
        />
      </Container>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f8f9fa' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 2, 
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
          >
            <Link component={RouterLink} to="/" color="inherit" underline="hover">
              Home
            </Link>
            {product.categories && product.categories[0] && (
              <Link 
                component={RouterLink} 
                to={`/category/${product.categories[0].id}`} 
                color="inherit"
                underline="hover"
              >
                {product.categories[0].name}
              </Link>
            )}
            <Typography color="text.primary">{product.name}</Typography>
          </Breadcrumbs>
        </Paper>
        
        {/* Main two-column layout */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            mb: 4
          }}
        >
          {/* Left Column - Product Image (fixed width) */}
          <Box 
            sx={{ 
              width: { md: '50%' },
              position: { md: 'sticky' },
              top: 20,
              alignSelf: 'flex-start'
            }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                overflow: 'hidden',
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: { xs: '300px', md: '450px' },
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {product.imageUrl ? (
                <Box 
                  component="img"
                  src={product.imageUrl}
                  alt={product.name}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Box 
                  sx={{ 
                    bgcolor: '#f5f5f5', 
                    width: '100%', 
                    height: '100%', 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    No image available
                  </Typography>
                </Box>
              )}
            </Paper>
            
            {/* Product Gallery - if multiple images */}
            {product.images && product.images.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                {product.images.map((image, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      component="img"
                      src={image.url}
                      alt={`${product.name} - image ${index + 1}`}
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                        }
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
          
          {/* Right Column - Product Details (fixed width) */}
          <Box 
            sx={{ 
              width: { md: '50%' },
              backgroundColor: 'white',
              borderRadius: 2,
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Title with favorite button */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
                {product.name}
              </Typography>
              <FavoriteButton 
                productId={product.id}
                initialFavorite={product.is_favorite}
                size="large"
              />
            </Box>
            
            {/* SKU & Availability */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                SKU: {product.sku || 'N/A'}
              </Typography>
              <Divider orientation="vertical" flexItem />
              {product.inStock ? (
                <Chip 
                  label="In Stock" 
                  color="success" 
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              ) : (
                <Chip 
                  label="Out of Stock" 
                  color="error" 
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Stack>
            
            {/* Categories & Tags */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {product.categories && product.categories.map((category) => (
                <Chip 
                  key={category.id}
                  label={category.name}
                  size="small"
                  component={RouterLink}
                  to={`/category/${category.id}`}
                  clickable
                  sx={{ 
                    bgcolor: 'rgba(46, 125, 50, 0.08)',
                    color: 'primary.dark'
                  }}
                />
              ))}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Price */}
            <Card variant="outlined" sx={{ mb: 3, bgcolor: 'rgba(46, 125, 50, 0.03)' }}>
              <CardContent>
                <Typography 
                  variant="h4" 
                  component="div" 
                  color="primary.dark"
                  sx={{ 
                    fontWeight: 700, 
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1
                  }}
                >
                  ${product.price.toFixed(2)}
                  {product.compareToPrice && product.compareToPrice > product.price && (
                    <Typography 
                      component="span" 
                      sx={{ 
                        textDecoration: 'line-through',
                        color: 'text.secondary',
                        fontWeight: 400,
                        fontSize: '1rem'
                      }}
                    >
                      ${product.compareToPrice.toFixed(2)}
                    </Typography>
                  )}
                </Typography>
                
                {/* Price breakdown with fees */}
                {fees && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {fees.deposit > 0  && ` + $${fees.deposit.toFixed(2)} deposit`}
                      {fees.ecoFee > 0 && ` + $${fees.ecoFee.toFixed(2)} eco fee`}
                    </Typography>

                    {/* Hide the total price for now */}
                    {/* <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                      Total: ${fees.total.toFixed(2)}
                    </Typography> */}
                  </Box>
                )}
              </CardContent>
            </Card>
            
            {/* Stock Information */}
            <Box sx={{ mb: 3 }}>
              {/* TODO: Add unlimited availability back in when inventory supports it */}
              {/* {product.inStock && product.unlimited && (
                <Typography variant="body2" color="text.secondary">
                  Unlimited availability
                </Typography>
              )} */}
              
              {product.inStock && !product.unlimited && product.quantity !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  {product.quantity < 10 ? (
                    <span style={{ color: '#d32f2f' }}>Only {product.quantity} left in stock</span>
                  ) : (
                    `${product.quantity} available`
                  )}
                </Typography>
              )}
            </Box>
            
            {/* Add to cart button */}
            <Box sx={{ mb: 4 }}>
              <AddToCartButton
                product={product}
                variant="contained"
                buttonSize="large"
                fullWidth
                showIcon
              />
            </Box>
            
            {/* Product tabs for details, specifications, and shipping */}
            <Box sx={{ mt: 3 }}>
              <Accordion 
                defaultExpanded 
                elevation={0}
                sx={{ 
                  mb: 2, 
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="description-content"
                  id="description-header"
                  sx={{ 
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography fontWeight={600}>Product Description</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box 
                    dangerouslySetInnerHTML={{ __html: product.description || '' }}
                    sx={{ 
                      '& img': { maxWidth: '100%', height: 'auto' },
                      '& a': { color: 'primary.main' }
                    }}
                  />
                </AccordionDetails>
              </Accordion>
              
              <Accordion 
                elevation={0}
                sx={{ 
                  mb: 2,
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="details-content"
                  id="details-header"
                  sx={{ 
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography fontWeight={600}>Specifications</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Box}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ width: '40%', fontWeight: 500 }}>
                            SKU
                          </TableCell>
                          <TableCell>{product.sku || 'N/A'}</TableCell>
                        </TableRow>
                        {product.attributes && product.attributes.map((attr, index) => (
                          <TableRow key={index}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                              {attr.name}
                            </TableCell>
                            <TableCell>
                              {Number(attr.value).toFixed(5).replace(/\.?0+$/, '')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
              
              <Accordion 
                elevation={0}
                sx={{ 
                  mb: 2,
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px !important',
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="shipping-content"
                  id="shipping-header"
                  sx={{ 
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography fontWeight={600}>Shipping & Returns</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Free shipping on orders over $99.
                  </Typography>
                  <Typography paragraph>
                    Returns accepted within 30 days of delivery. Items must be in resellable condition.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        </Box>
        
        {/* Debug Information */}
        {isDebugMode && (
          <Accordion
            sx={{ 
              mt: 4, 
              border: '1px dashed #f50057',
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="debug-content"
              id="debug-header"
              sx={{ 
                bgcolor: 'rgba(245, 0, 87, 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon color="error" />
                <Typography fontWeight={600} color="error">
                  Debug Information
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Environment Variables
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Variable</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>REACT_APP_DEBUG_MODE</TableCell>
                        <TableCell>{process.env.REACT_APP_DEBUG_MODE || 'undefined'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>REACT_APP_API_URL</TableCell>
                        <TableCell>{process.env.REACT_APP_API_URL || 'undefined'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>NODE_ENV</TableCell>
                        <TableCell>{process.env.NODE_ENV || 'undefined'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  API Response
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3, maxHeight: '400px', overflow: 'auto' }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(product, null, 2)}
                  </pre>
                </Paper>
                
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Fee Calculations
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Fee Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Logic</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Base Price</TableCell>
                        <TableCell>${product.price.toFixed(2)}</TableCell>
                        <TableCell>Product price from API</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Tax (GST + PST)</TableCell>
                        <TableCell>${fees?.tax.toFixed(2)}</TableCell>
                        <TableCell>GST (5%): ${fees?.gst.toFixed(2)}, PST (7%): ${fees?.pst.toFixed(2)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Deposit</TableCell>
                        <TableCell>${fees?.deposit.toFixed(2)}</TableCell>
                        <TableCell>
                          {fees && fees.deposit > 0 
                            ? 'Applied - Category contains "bottle" or "can"' 
                            : 'Not applied - No matching category'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Eco Fee</TableCell>
                        <TableCell>${fees?.ecoFee.toFixed(2)}</TableCell>
                        <TableCell>
                          {fees && fees.ecoFee > 0 
                            ? 'Applied - Category contains "plastic" or "glass"' 
                            : 'Not applied - No matching category'}
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>${fees?.total.toFixed(2)}</TableCell>
                        <TableCell>Sum of all values</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Cart Information
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.03)' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Item in Cart</TableCell>
                        <TableCell>{cartItem ? 'Yes' : 'No'}</TableCell>
                      </TableRow>
                      {cartItem && (
                        <>
                          <TableRow>
                            <TableCell>Quantity in Cart</TableCell>
                            <TableCell>{cartItem.quantity}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Line Item Total</TableCell>
                            <TableCell>${(cartItem.quantity * cartItem.price).toFixed(2)}</TableCell>
                          </TableRow>
                        </>
                      )}
                      <TableRow>
                        <TableCell>Cart Item Object</TableCell>
                        <TableCell>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                            {cartItem ? JSON.stringify(cartItem, null, 2) : 'null'}
                          </pre>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Container>
    </Box>
  );
};

export default ProductPage; 